import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Search, Eye, Package, Truck, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronUp, MapPin, Mail, Phone, DollarSign, CreditCard,
  Wallet, RefreshCw, Send, Repeat
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const orderStatuses = [
  { value: 'pending', label: 'Pending', icon: Clock, color: 'bg-yellow-100 text-yellow-800' },
  { value: 'awaiting_payment', label: 'Awaiting Payment', icon: Wallet, color: 'bg-orange-100 text-orange-800' },
  { value: 'paid', label: 'Paid', icon: CheckCircle, color: 'bg-green-100 text-green-800' },
  { value: 'processing', label: 'Processing', icon: Package, color: 'bg-blue-100 text-blue-800' },
  { value: 'shipped', label: 'Shipped', icon: Truck, color: 'bg-purple-100 text-purple-800' },
  { value: 'delivered', label: 'Delivered', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-800' },
  { value: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'bg-red-100 text-red-800' },
  { value: 'refunded', label: 'Refunded', icon: XCircle, color: 'bg-gray-100 text-gray-800' },
];

const paymentMethodIcons = {
  card: CreditCard,
  cashapp: DollarSign,
  venmo: Wallet,
  demo: Clock
};

const paymentMethodLabels = {
  card: 'Credit Card',
  cashapp: 'CashApp',
  venmo: 'Venmo',
  demo: 'Demo'
};

const paymentMethodColors = {
  card: 'bg-slate-100 text-slate-700',
  cashapp: 'bg-green-100 text-green-700',
  venmo: 'bg-blue-100 text-blue-700',
  demo: 'bg-gray-100 text-gray-500'
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      // Fetch from payments orders endpoint (where checkout orders are stored)
      const response = await axios.get(`${API}/payments/orders`);
      let allOrders = response.data?.orders || [];
      
      // Filter by status if needed
      if (statusFilter !== 'all') {
        allOrders = allOrders.filter(o => o.status === statusFilter);
      }
      
      setOrders(allOrders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast({
        title: 'Error',
        description: 'Failed to load orders',
        variant: 'destructive'
      });
    }
    setLoading(false);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setTrackingNumber(order.tracking_number || '');
    setIsDetailOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;
    try {
      await axios.patch(`${API}/payments/orders/${selectedOrder.id}/status?status=${newStatus}`);
      toast({ title: 'Order Updated', description: `Order status changed to ${newStatus}` });
      fetchOrders();
      setIsDetailOpen(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update order', variant: 'destructive' });
    }
  };

  const handleToggleRecurring = async (order, isRecurring) => {
    try {
      await axios.put(`${API}/store/orders/${order.id}/recurring?is_recurring=${isRecurring}&interval_days=30`);
      toast({ 
        title: isRecurring ? 'Recurring Enabled' : 'Recurring Disabled', 
        description: isRecurring ? 'Customer will receive invoices for this order' : 'Recurring invoices disabled' 
      });
      fetchOrders();
    } catch (error) {
      console.error('Failed to toggle recurring:', error);
      toast({ title: 'Error', description: 'Failed to update recurring status', variant: 'destructive' });
    }
  };

  const handleSendRecurringInvoice = async (order) => {
    if (!window.confirm(`Send a recurring invoice to ${order.customer_name} for ${order.total?.toFixed(2) || '0.00'}?`)) return;
    try {
      const response = await axios.post(`${API}/store/orders/${order.id}/send-recurring-invoice`);
      toast({ 
        title: 'Invoice Sent', 
        description: `New order ${response.data.new_order?.order_number} created awaiting payment` 
      });
      fetchOrders();
    } catch (error) {
      console.error('Failed to send invoice:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to send invoice', variant: 'destructive' });
    }
  };

  const getStatusInfo = (status) => {
    return orderStatuses.find(s => s.value === status) || orderStatuses[0];
  };

  const filteredOrders = orders.filter(order => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return order.order_number.toLowerCase().includes(query) ||
             order.customer_name.toLowerCase().includes(query) ||
             order.customer_email.toLowerCase().includes(query);
    }
    return true;
  });

  const orderCounts = {
    all: orders.length,
    awaiting_payment: orders.filter(o => o.status === 'awaiting_payment').length,
    pending: orders.filter(o => o.status === 'pending').length,
    paid: orders.filter(o => o.status === 'paid').length,
    processing: orders.filter(o => o.status === 'processing').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
  };

  // Get payment method icon
  const getPaymentIcon = (method) => {
    return paymentMethodIcons[method] || CreditCard;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
        <p className="text-gray-500">Manage and fulfill customer orders</p>
      </div>

      {/* Status Tabs */}
      <div className="flex flex-wrap gap-2">
        {['all', 'awaiting_payment', 'paid', 'processing', 'shipped', 'delivered'].map((status) => (
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'outline'}
            onClick={() => setStatusFilter(status)}
            className={statusFilter === status 
              ? 'bg-purple-600 hover:bg-purple-700 text-white' 
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900'}
          >
            {status === 'awaiting_payment' ? 'Awaiting Payment' : status.charAt(0).toUpperCase() + status.slice(1)}
            <Badge className={statusFilter === status ? 'ml-2 bg-white/20' : 'ml-2 bg-purple-100 text-purple-700'}>{orderCounts[status] || 0}</Badge>
          </Button>
        ))}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by order number, customer name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#6e2ea8] border-t-transparent rounded-full" />
            </div>
          ) : filteredOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Order</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Source</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Customer</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Items</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Payment</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Status</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-500">Recurring</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Date</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Total</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const statusInfo = getStatusInfo(order.status);
                    const PaymentIcon = getPaymentIcon(order.payment_method);
                    return (
                      <tr key={order.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <span className="font-bold text-gray-900">{order.order_number}</span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={order.source === 'pos' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}>
                            {order.source === 'pos' ? 'POS' : 'Web'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-800">{order.customer_name}</p>
                            <p className="text-sm text-gray-500">{order.customer_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-600">{order.items?.length || 0} items</span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={paymentMethodColors[order.payment_method] || 'bg-gray-100 text-gray-600'}>
                            <PaymentIcon className="w-3 h-3 mr-1" />
                            {paymentMethodLabels[order.payment_method] || order.payment_method}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={statusInfo.color}>
                            <statusInfo.icon className="w-3 h-3 mr-1" />
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <Switch
                              checked={order.is_recurring || false}
                              onCheckedChange={(checked) => handleToggleRecurring(order, checked)}
                              className="data-[state=checked]:bg-[#6e2ea8]"
                            />
                            {order.is_recurring && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleSendRecurringInvoice(order)}
                                className="text-[#6e2ea8] hover:text-[#5a2590] hover:bg-purple-50 p-1"
                                title="Send recurring invoice"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-500">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-gray-900">
                          ${order.total?.toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button variant="outline" size="sm" onClick={() => handleViewOrder(order)}>
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No orders found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order {selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Status */}
              <div className="flex flex-wrap gap-4 items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Current Status</p>
                  <Badge className={getStatusInfo(selectedOrder.status).color + " text-lg px-3 py-1"}>
                    {getStatusInfo(selectedOrder.status).label}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Method</p>
                  <Badge className={paymentMethodColors[selectedOrder.payment_method] + " text-lg px-3 py-1"}>
                    {paymentMethodLabels[selectedOrder.payment_method] || selectedOrder.payment_method}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Order Date</p>
                  <p className="font-medium">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="text-xl font-bold text-purple-600">${selectedOrder.total?.toFixed(2)}</p>
                </div>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Customer Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="font-medium">{selectedOrder.customer_name}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      <Mail className="w-4 h-4" /> {selectedOrder.customer_email}
                    </p>
                    {(selectedOrder.shipping?.phone || selectedOrder.shipping_address?.phone) && (
                      <p className="text-sm text-gray-500 flex items-center gap-2">
                        <Phone className="w-4 h-4" /> {selectedOrder.shipping?.phone || selectedOrder.shipping_address?.phone}
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Shipping Address</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>
                        {selectedOrder.shipping?.address1 || selectedOrder.shipping_address?.address}<br />
                        {selectedOrder.shipping?.address2 && <>{selectedOrder.shipping.address2}<br /></>}
                        {selectedOrder.shipping?.city || selectedOrder.shipping_address?.city}, {selectedOrder.shipping?.state || selectedOrder.shipping_address?.state} {selectedOrder.shipping?.zipCode || selectedOrder.shipping_address?.zip_code}
                      </span>
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Order Items */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Order Items</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="divide-y">
                    {selectedOrder.items?.map((item, index) => (
                      <div key={index} className="py-3 flex items-center gap-4">
                        {item.image && (
                          <img src={item.image} alt={item.name || item.product_name} className="w-12 h-12 rounded object-cover" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{item.name || item.product_name}</p>
                          {(item.selected_strength || item.selected_package) && (
                            <p className="text-sm text-purple-600">{item.selected_strength} • {item.selected_package}</p>
                          )}
                          <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                        </div>
                        <span className="font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-4 mt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Subtotal</span>
                      <span>${selectedOrder.subtotal?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Shipping</span>
                      <span>{selectedOrder.shipping_cost === 0 ? 'FREE' : `$${selectedOrder.shipping_cost?.toFixed(2)}`}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Tax</span>
                      <span>${selectedOrder.tax?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span className="text-purple-600">${selectedOrder.total?.toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Update Status */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Update Order Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={newStatus} onValueChange={setNewStatus}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {orderStatuses.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tracking Number</Label>
                      <Input
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        placeholder="Enter tracking number"
                      />
                    </div>
                  </div>
                  <Button onClick={handleUpdateStatus} className="bg-purple-600 hover:bg-purple-700">
                    Update Order
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrders;
